import { CatalogEntityType } from "../types";
import { resolveEnrollmentStatusKey } from "../UsersAndGroups/config/enrollmentStatus";
import {
  TRACKING_EDITABLE_STATUS_KEYS,
  TRACKING_KIND_BY_CATEGORY,
  TrackingKind,
} from "./config/trackingConfig";
import { ProgressCounts, ProgressNode } from "./types";

export const countDescendants = (node: ProgressNode): ProgressCounts =>
  node.children.reduce<ProgressCounts>(
    (acc, child) => {
      const nested = countDescendants(child);
      return {
        activities:
          acc.activities +
          nested.activities +
          (child.entityType === "activity" ? 1 : 0),
        courses:
          acc.courses +
          nested.courses +
          (child.entityType === "course" ? 1 : 0),
      };
    },
    { activities: 0, courses: 0 },
  );

export const isNodeCompleted = (node: ProgressNode): boolean =>
  resolveEnrollmentStatusKey(node) === "completed";

export const isNodeExempt = (node: ProgressNode): boolean =>
  resolveEnrollmentStatusKey(node) === "exempt";

export const isNodeSelectable = (node: ProgressNode): boolean =>
  !isNodeCompleted(node) && node.forceable;

const collectInheritedKeys = (node: ProgressNode): string[] =>
  node.children.flatMap((child) =>
    child.forceableFromParent
      ? [
          ...(isNodeSelectable(child) ? [child.key] : []),
          ...collectInheritedKeys(child),
        ]
      : [],
  );

export const collectSelectableKeys = (node: ProgressNode): string[] => [
  ...(isNodeSelectable(node) ? [node.key] : []),
  ...collectInheritedKeys(node),
];

const collectCoveredKeys = (
  node: ProgressNode,
  selected: Set<string>,
  isCovered: boolean,
): string[] =>
  node.children.flatMap((child) => {
    const isChildCovered =
      child.forceableFromParent && (isCovered || selected.has(node.key));
    return [
      ...(isChildCovered && !isNodeCompleted(child) ? [child.key] : []),
      ...collectCoveredKeys(child, selected, isChildCovered),
    ];
  });

export const getCascadeCoveredKeys = (
  root: ProgressNode,
  selected: Set<string>,
): Set<string> => new Set(collectCoveredKeys(root, selected, false));

const findNodeChain = (root: ProgressNode, key: string): ProgressNode[] => {
  if (root.key === key) return [root];
  const nested = root.children
    .map((child) => findNodeChain(child, key))
    .find((chain) => chain.length > 0);
  return nested ? [root, ...nested] : [];
};

const collectCascadeAncestorKeys = (chain: ProgressNode[]): string[] => {
  const ancestors = chain.slice(0, -1);
  const node = chain[chain.length - 1];
  return node?.forceableFromParent && ancestors.length > 0
    ? [
        ancestors[ancestors.length - 1].key,
        ...collectCascadeAncestorKeys(ancestors),
      ]
    : [];
};

export const toggleNodeSelection = (
  selected: Set<string>,
  node: ProgressNode,
  root: ProgressNode,
): Set<string> => {
  const next = new Set<string>();
  selected.forEach((key) => next.add(key));
  if (selected.has(node.key)) {
    [
      ...collectSelectableKeys(node),
      ...collectCascadeAncestorKeys(findNodeChain(root, node.key)),
    ].forEach((key) => next.delete(key));
  } else {
    collectSelectableKeys(node).forEach((key) => next.add(key));
  }
  return next;
};

const collectExplicitDescendants = (
  node: ProgressNode,
  selected: Set<string>,
): ProgressNode[] =>
  node.children.flatMap((child) =>
    child.forceableFromParent
      ? collectExplicitDescendants(child, selected)
      : getEffectiveSelection(child, selected),
  );

export const getEffectiveSelection = (
  node: ProgressNode,
  selected: Set<string>,
): ProgressNode[] =>
  selected.has(node.key)
    ? [node, ...collectExplicitDescendants(node, selected)]
    : node.children.flatMap((child) => getEffectiveSelection(child, selected));

export const summarizeSelection = (
  nodes: ProgressNode[],
): Record<CatalogEntityType, number> =>
  nodes.reduce<Record<CatalogEntityType, number>>(
    (acc, node) => ({ ...acc, [node.entityType]: acc[node.entityType] + 1 }),
    { path: 0, course: 0, activity: 0 },
  );

export const MIXED_STATUS = "MIXED";

export const getCommonStatus = (nodes: ProgressNode[]): string | null => {
  const statuses = Array.from(new Set(nodes.map((node) => node.status ?? "")));
  if (statuses.length === 0) return null;
  return statuses.length === 1 ? statuses[0] || null : MIXED_STATUS;
};

export const getTrackingKind = (node: ProgressNode): TrackingKind | null =>
  node.entityType === "activity" && node.category
    ? (TRACKING_KIND_BY_CATEGORY[node.category] ?? null)
    : null;

export const canEditTracking = (node: ProgressNode): boolean =>
  getTrackingKind(node) !== null &&
  TRACKING_EDITABLE_STATUS_KEYS.includes(resolveEnrollmentStatusKey(node));
